import React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';

const cardVariants = cva(
  "rounded-xl border bg-card text-card-foreground transition-all duration-200",
  {
    variants: {
      variant: {
        default: "bg-white border-gray-200 dark:bg-dark-100 dark:border-dark-200",
        elevated: "bg-white border-gray-200 shadow-md dark:bg-dark-100 dark:border-dark-200 dark:shadow-lg",
        "dark-accent": "bg-dark-100 border-dark-200 dark:border-accent-500/20",
        "dark-gradient": "bg-gradient-to-br from-dark-100 to-dark-200 border-dark-300",
      },
      padding: {
        none: "p-0",
        sm: "p-4",
        default: "p-6",
        lg: "p-8",
      },
      shadow: {
        none: "shadow-none",
        sm: "shadow-sm",
        soft: "shadow-soft",
        lg: "shadow-lg",
        glow: "dark:shadow-glow",
      },
      hover: {
        none: "",
        lift: "hover:shadow-lg hover:-translate-y-1 cursor-pointer",
        glow: "hover:shadow-glow cursor-pointer dark:hover:shadow-cyan-glow",
        accent: "hover:border-accent-500/50 cursor-pointer dark:hover:shadow-glow",
      },
    },
    defaultVariants: {
      variant: "default",
      padding: "default",
      shadow: "soft",
      hover: "none",
    },
  }
);

export const Card = React.forwardRef(({
  className,
  variant,
  padding,
  shadow,
  hover,
  children,
  ...props
}, ref) => (
  <div
    ref={ref}
    className={cn(cardVariants({ variant, padding, shadow, hover }), className)}
    {...props}
  >
    {children}
  </div>
));

Card.displayName = "Card";

// Card Header Component
export const CardHeader = React.forwardRef(({
  className,
  children,
  ...props
}, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-6 pb-4", className)}
    {...props}
  >
    {children}
  </div>
));

CardHeader.displayName = "CardHeader";

// Card Title Component
export const CardTitle = React.forwardRef(({
  className,
  children,
  ...props
}, ref) => (
  <h3
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight text-gray-900 dark:text-dark-900", className)}
    {...props}
  >
    {children}
  </h3>
));

CardTitle.displayName = "CardTitle";

// Card Description Component
export const CardDescription = React.forwardRef(({
  className,
  children,
  ...props
}, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-gray-600 dark:text-dark-600", className)}
    {...props}
  >
    {children}
  </p>
));

CardDescription.displayName = "CardDescription";

// Card Content Component
export const CardContent = React.forwardRef(({
  className,
  children,
  ...props
}, ref) => (
  <div
    ref={ref}
    className={cn("p-6 pt-0", className)}
    {...props}
  >
    {children}
  </div>
));

CardContent.displayName = "CardContent";

// Card Footer Component
export const CardFooter = React.forwardRef(({
  className,
  children,
  ...props
}, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-4 gap-3", className)}
    {...props}
  >
    {children}
  </div>
));

CardFooter.displayName = "CardFooter"; 